import React from 'react'
import * as Sentry from '@sentry/nextjs'
import styled, { css } from 'styled-components'
import SwiperCore, { Navigation, Pagination, EffectFade } from 'swiper'
import { useInView } from 'react-intersection-observer'
import { useForm } from 'react-hook-form'
import { FadeInArea } from '../styles/Component'
import { breakpoint } from '../layout/styles'
import { Image } from '../styles/Banner'
import getConfig from 'next/config'

SwiperCore.use([Navigation, Pagination, EffectFade])

const Newsletter = ({ item }: any) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 })
  const {
    register,
    handleSubmit,
    formState,
    formState: { errors }
  } = useForm({ mode: 'onChange' })
  const [success, setSuccess] = React.useState<boolean>(false)
  const { publicRuntimeConfig } = getConfig()

  const onSubmit = (data: any) => {
    const payload: any = {
      email: data.email
    }

    if (publicRuntimeConfig.segmentNewsletter) {
      payload.name = data.name
      payload.segment = data.segment
    }

    const options = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }

    fetch(`/api/subscribe?list=${item.klaviyo.list_id}`, options)
      .then((response) => response.json())
      .then(() => setSuccess(true))
      .catch((err) => {
        console.error(err)
        Sentry.captureException(err)
      })
  }

  if (!item) return <></>

  return (
    <Container bg={item.background?.url} ref={ref} inView={inView} noprint>
      <Image bg={item.background?.url} left={true} />
      <Wrapper>
        <a id="newsletter" />
        <Content onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <h3>{item.title}</h3>
          </Row>
          <Row>
            <p>{item.description}</p>
          </Row>
          {!success && (
            <>
              <Row segment={publicRuntimeConfig?.segmentNewsletter}>
                {publicRuntimeConfig?.segmentNewsletter && (
                  <>
                    <label>
                      <span className="sr-only">
                        {item.segment_placeholder}
                      </span>
                      <select
                        {...register('segment', { required: true })}
                        defaultValue=""
                      >
                        <option value="" disabled hidden>
                          {item.segment_placeholder}
                        </option>
                        {item.segments &&
                          item.segments
                            .split(/\n/gi)
                            .map((segment: any) => (
                              <option key={segment}>{segment}</option>
                            ))}
                      </select>
                    </label>
                    <label>
                      <span className="sr-only">{item.name_placeholder}</span>
                      <input
                        type="text"
                        {...register('name', { required: true })}
                        placeholder={item.name_placeholder}
                      />
                    </label>
                  </>
                )}
                <label>
                  <span className="sr-only">{item.email_placeholder}</span>
                  <input
                    type="text"
                    {...register('email', {
                      required: true,
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                        message: 'Invalid email'
                      }
                    })}
                    placeholder={item.email_placeholder}
                  />
                </label>
              </Row>
              <Row style={{ maxWidth: '380px' }}>
                <label>
                  <input
                    type="checkbox"
                    {...register('disclaimer', { required: true })}
                    value="1"
                  />
                  <span dangerouslySetInnerHTML={{ __html: item.disclaimer }} />
                </label>
              </Row>
              <Row>
                <input
                  type="submit"
                  disabled={!formState.isValid}
                  value={item.call_to_action_text}
                />
              </Row>
            </>
          )}
          {success && (
            <Success>
              <img
                src="/images/check.svg"
                width={24}
                height={24}
                alt={item.thank_you}
              />{' '}
              {item.thank_you}
            </Success>
          )}
        </Content>
      </Wrapper>
    </Container>
  )
}

export default Newsletter

export const Wrapper = styled.div`
  max-width: 1120px;
  margin: 1.5rem;
  color: var(--text);
  @media ${breakpoint.desktop} {
    margin: 0 auto;
    color: black;
  }
`

export const Row = styled.div<any>`
  margin: 1rem 0;
  @media ${breakpoint.desktop} {
    max-width: 320px;
  }
  ${(props) =>
    props.segment &&
    css`
      width: 100%;
      display: flex;
      flex-direction: column;
      @media ${breakpoint.desktop} {
        flex-direction: row;
      }

      label {
        margin-bottom: 16px;
        @media ${breakpoint.desktop} {
          margin-bottom: 0;
          width: 33.3333%;
          margin-right: 16px;
          &:last-child {
            margin-right: 0;
          }
        }
        input[type='text'],
        select {
          width: 100%;
          max-width: 100%;
          font-family: GothamMedium, "Open Sans", sans-serif;
          color: rgba(0, 0, 0, 0.6);
        }
      }
    `}
`

const Success = styled.div`
  text-align: center;
  padding: 0 0 4rem;
  max-width: 310px;
  font-size: 24px;
  display: flex;
  align-items: center;
  font-weight: 700;
  img {
    margin-right: 10px;
  }
`

export const Container = styled<any>(FadeInArea)`
  padding: 20px 0;
  @media ${breakpoint.desktop} {
    background: url('${(props) =>
        props.bg}?height=${400}&auto=webp&format=pjpg&quality=75')
      no-repeat scroll center center;
    background-size: cover;
  }
  color: black;
  h3 {
    font-size: 36px;
    font-weight: 700;
    line-height: 46px;
    margin: 0;
  }
  p {
    font-size: 16px;
    font-weight: 400;
    line-height: 21px;
  }

  input[type='text'],
  select {
    width: 100%;
    font-size: 16px;
    font-weight: 400;
    line-height: 21px;
    padding: 10px 12px;
    max-width: 310px;
  }

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    margin: 0 5px 0 0;
    border: 1px black solid;
    background: #fff;
    position: relative;
    top: 2px;
  }

  label {
    font-size: 16px;
    font-weight: 400;
    line-height: 21px;
    a {
      font-weight: bold;
      color: var(--text);
      @media ${breakpoint.desktop} {
        color: black;
      }
    }
  }

  input[type='submit'] {
    border: 0;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 11px 20px;
    background: var(--button-bg, var(--primary));
    color: white;
    font-size: 15px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: 0.05em;
    text-align: center;
    text-transform: uppercase;
    &:disabled {
      background: #eaeaea;
      color: rgba(102, 102, 102, 1);
    }
    &:not(:disabled) {
      cursor: pointer;
    }
  }
  ${(props) =>
    props.noprint &&
    `
    @media print {
      display: none;
    }
  `}
`

export const Content = styled.form<any>`
  @media ${breakpoint.desktop} {
    max-width: 380px;
  }
`
